import { isIsoDay, partsOf } from "~/hec/utils/utc-date";

/**
 * Time math for the FSA deadline ticker (COM-472 / COM-475 / COM-476). A
 * deadline is a calendar day and runs to local midnight after it, in the
 * shopper's own timezone: the one deliberate LOCAL exception to `utc-date.ts`.
 * `utc` is for the pre-hydration render only, so server and browser agree.
 */

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** Epoch ms of local (or UTC) midnight after `ymd`, or null for anything but a real day. */
export function deadlineEnd(ymd: string, utc = false): number | null {
  if (!isIsoDay(ymd)) return null;
  const { year, month, day } = partsOf(ymd);
  return utc
    ? Date.UTC(year, month, day + 1)
    : new Date(year, month, day + 1).getTime();
}

/**
 * Time left until `end`, or null once it has passed. Seconds round UP, so a
 * running countdown never reads 0/0/0/0.
 */
export function countdownTo(end: number, now: number): Countdown | null {
  const total = Math.ceil((end - now) / 1000);
  if (total <= 0) return null;
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor(total / 3600) % 24,
    minutes: Math.floor(total / 60) % 60,
    seconds: total % 60,
  };
}

/**
 * The earliest deadline still ahead of `now`, so a multi-plan shopper moves on
 * to the next plan at zero. When none is ahead: the latest (passed state),
 * however long ago. Null only when no date parses.
 */
export function pickDeadline(
  dates: readonly string[],
  now: number,
  utc = false,
): { date: string; end: number } | null {
  const dated = dates
    .map(date => ({ date, end: deadlineEnd(date, utc) }))
    .filter((d): d is { date: string; end: number } => d.end !== null)
    .sort((a, b) => a.end - b.end);
  const next = dated.find(d => d.end > now);
  if (next) return next;
  return dated.at(-1) ?? null;
}
